import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Briefcase, Edit, Trash2, Eye, EyeOff, CheckCircle, 
  Loader2, Search, Shield, Globe, Phone, MapPin, Crown, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';
import WorkshopEditor from './WorkshopEditor';

export default function AdminWorkshops() {
  var [workshops, setWorkshops] = useState([]);
  var [loading, setLoading] = useState(true);
  var [searchTerm, setSearchTerm] = useState('');
  var [filterType, setFilterType] = useState('all');
  var [sortBy, setSortBy] = useState('recent');
  var [editingWorkshop, setEditingWorkshop] = useState(null);

  useEffect(function() {
    loadWorkshops();
  }, []);

  async function loadWorkshops() {
    try {
      var res = await authAxios.get(API + '/workshops/public?limit=200');
      setWorkshops(res.data.workshops || []);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando talleres');
    }
    setLoading(false);
  }

  async function handleDelete(workshopId) {
    if (!window.confirm('¿Eliminar este taller?')) return;
    try {
      await authAxios.delete(API + '/admin/workshops/' + workshopId, { withCredentials: true });
      toast.success('Taller eliminado');
      loadWorkshops();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  }

  async function toggleVisibility(workshopId, isPublic) {
    try {
      await authAxios.put(API + '/admin/workshops/' + workshopId, { is_public: !isPublic }, { withCredentials: true });
      toast.success(isPublic ? 'Taller ocultado' : 'Taller ahora es público');
      loadWorkshops();
    } catch (e) {
      toast.error('Error al cambiar visibilidad');
    }
  }

  async function toggleVerified(workshopId, isVerified) {
    try {
      await authAxios.put(API + '/admin/workshops/' + workshopId, { is_verified: !isVerified }, { withCredentials: true });
      toast.success(isVerified ? 'Verificación removida' : 'Taller verificado');
      loadWorkshops();
    } catch (e) {
      toast.error('Error');
    }
  }

  var filteredWorkshops = workshops.filter(function(w) {
    var matchesSearch = !searchTerm || 
      (w.name && w.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (w.city && w.city.toLowerCase().includes(searchTerm.toLowerCase()));
    var matchesType = filterType === 'all' || w.type === filterType || w.workshop_type === filterType;
    return matchesSearch && matchesType;
  });

  // Sort
  filteredWorkshops = [...filteredWorkshops].sort(function(a, b) {
    if (sortBy === 'recent') {
      var dateA = a.created_at || a.updated_at || '';
      var dateB = b.created_at || b.updated_at || '';
      return dateB.localeCompare(dateA);
    } else if (sortBy === 'name') {
      return (a.name || '').localeCompare(b.name || '');
    } else if (sortBy === 'verified') {
      return (b.is_verified ? 1 : 0) - (a.is_verified ? 1 : 0);
    }
    return 0;
  });

  var workshopTypes = ['all', 'Prevención', 'Tratamiento', 'Rehabilitación', 'Apoyo Familiar', 'Grupos de Ayuda', 'Capacitación', 'Otro'];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // If editing, show full-page editor
  if (editingWorkshop) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-950 overflow-auto">
        <WorkshopEditor 
          workshop={editingWorkshop}
          onSave={function() { setEditingWorkshop(null); loadWorkshops(); }}
          onCancel={function() { setEditingWorkshop(null); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-2 flex-1 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar talleres..."
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
              data-testid="search-workshops"
            />
          </div>
          <select
            value={filterType}
            onChange={function(e) { setFilterType(e.target.value); }}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-slate-300"
            data-testid="filter-workshop-type"
          >
            {workshopTypes.map(function(type) {
              return (
                <option key={type} value={type}>
                  {type === 'all' ? 'Todos los tipos' : type}
                </option>
              );
            })}
          </select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700 text-slate-300" data-testid="sort-workshops">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="recent" className="text-slate-300">Más recientes</SelectItem>
              <SelectItem value="name" className="text-slate-300">Nombre A-Z</SelectItem>
              <SelectItem value="verified" className="text-slate-300">Verificados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{workshops.length}</p>
              <p className="text-slate-400 text-sm">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{workshops.filter(function(w) { return w.is_public !== false; }).length}</p>
              <p className="text-slate-400 text-sm">Públicos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">{workshops.filter(function(w) { return w.is_verified; }).length}</p>
              <p className="text-slate-400 text-sm">Verificados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">{workshops.filter(function(w) { return w.is_premium || w.plan_level > 0; }).length}</p>
              <p className="text-slate-400 text-sm">Premium</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workshops List */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Talleres ({filteredWorkshops.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredWorkshops.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay talleres</p>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredWorkshops.map(function(workshop) {
                return (
                  <div key={workshop.workshop_id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-white font-medium truncate">{workshop.name || 'Sin nombre'}</h4>
                          {(workshop.is_premium || workshop.plan_level > 0) && (
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                              <Crown className="w-3 h-3" /> Premium
                            </span>
                          )}
                          {workshop.is_verified && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
                              <Shield className="w-3 h-3" /> Verificado
                            </span>
                          )}
                          {workshop.is_public === false && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                              Oculto
                            </span>
                          )}
                        </div>
                        {(workshop.type || workshop.workshop_type) && (
                          <p className="text-emerald-400 text-sm mt-1">{workshop.type || workshop.workshop_type}</p>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-400">
                          {workshop.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {workshop.city}
                            </span>
                          )}
                          {workshop.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {workshop.phone}
                            </span>
                          )}
                          {workshop.modality && (
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" /> {workshop.modality}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={function() { toggleVisibility(workshop.workshop_id, workshop.is_public !== false); }}
                          className="text-slate-400 hover:text-white"
                          title={workshop.is_public !== false ? 'Ocultar' : 'Mostrar'}
                        >
                          {workshop.is_public !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={function() { toggleVerified(workshop.workshop_id, workshop.is_verified); }}
                          className={workshop.is_verified ? 'text-blue-400' : 'text-slate-400 hover:text-white'}
                          title={workshop.is_verified ? 'Quitar verificación' : 'Verificar'}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={function() { setEditingWorkshop(workshop); }}
                          className="text-slate-400 hover:text-white"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={function() { handleDelete(workshop.workshop_id); }}
                          className="text-slate-400 hover:text-red-400"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
